import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import "./AddList.css";
// import "./AddList.generated-url.css";

const AdList = () => {
  const { pubId } = useParams();
  const navigate = useNavigate();

  const [adList, setAdList] = useState([]);
  const [scriptUrl, setScriptUrl] = useState(""); // From listing API response.script
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [generatedFile, setGeneratedFile] = useState(null);
  const [generatingType, setGeneratingType] = useState(null);
  const [generateError, setGenerateError] = useState("");
  const [copyMessage, setCopyMessage] = useState("");
  const generatedResultRef = useRef(null);

  // ==============================
  // Get Ad List
  // ==============================
  const alladList = async () => {
    setLoading(true);
    setScriptUrl("");
    try {
      const token = localStorage.getItem("token");

      const allAdsListRes = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/admin/getAllAdPlacements/${pubId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );

      if (allAdsListRes.status === 200) {
        setAdList(allAdsListRes?.data?.data || []);
        setScriptUrl(allAdsListRes?.data?.script || "");
      } else {
        setMessage("No ads found.");
      }
    } catch (error) {
      if (error.response?.status === 401) {
        console.error("Unauthorized! Token might be expired.");
        localStorage.removeItem("token");
        navigate("/admin");
      } else {
        console.error("Error fetching data:", error);
      }

      setMessage("Error fetching Ad list.");
    } finally {
      setLoading(false);
    }
  };

  // ==============================
  // Filter
  // ==============================
  const filteredAdList =
    adList?.length > 0
      ? adList.filter((ad) => {
          if (filterStatus === "all") return true;

          const statusLabel =
            ad.status === 0
              ? "Active"
              : ad.status === 1
              ? "Inactive"
              : ad.status;

          return statusLabel === filterStatus;
        })
      : [];

  // ==============================
  // Generate Tag from the already-loaded listing response (NO API call)
  // ==============================
  const handleGenerateTag = (adPlacement) => {
    setGeneratedFile(null);
    setGenerateError("");
    setCopyMessage("");

    // Prefer elmid when available, otherwise use elmcl.
    const elmid = typeof adPlacement?.elmid === "string" ? adPlacement.elmid.trim() : "";
    const elmcl = typeof adPlacement?.elmcl === "string" ? adPlacement.elmcl.trim() : "";

    if (!elmid && !elmcl) {
      setGenerateError("Cannot generate tag: both elmid and elmcl are missing for this ad placement.");
      return;
    }

    if (!scriptUrl) {
      setGenerateError("Cannot generate tag: script URL is missing in the ad listing API response.");
      return;
    }

    try {
      // Preserve any other query parameters and keep the existing ?v=1 behavior.
      const url = new URL(scriptUrl);
      if (!['https:', 'http:'].includes(url.protocol)) {
        throw new Error('Invalid script URL protocol');
      }
      // url.searchParams.set("v", "1");

      const escapeHtmlAttribute = (value) => value
        .replace(/&/g, "&amp;")
        .replace(/"/g, "&quot;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");

      const attributeName = elmid ? "id" : "class";
      const attributeValue = escapeHtmlAttribute(elmid || elmcl);
      const divTag = `<div ${attributeName}="${attributeValue}"></div>`;
      const scriptTag = `<script async type="text/javascript" data-ad="adChronix" src="${escapeHtmlAttribute(url.toString())}"></script>`;

      setGeneratedFile({
        type: "Tag",
        url: url.toString(),
        value: `${scriptTag}\n\n${divTag}`, // Blank line between div and script
        isTag: true,
      });
    } catch (error) {
      console.error("Unable to build tag:", error);
      setGenerateError("Cannot generate tag: invalid script URL in the ad listing API response.");
    }
  };

  // ==============================
  // Existing Generate JSON / Bundle JS buttons still call their APIs
  // ==============================
  const handleGenerate = async (type) => {
    if (generatingType) return;

    const isBundle = type === "bundle";
    const label = isBundle ? "Bundle JS" : "JSON";
    const token = localStorage.getItem("token");

    if (!token) {
      setGenerateError("No token found. Please log in again.");
      return;
    }

    setGeneratingType(type);
    setGeneratedFile(null);
    setGenerateError("");
    setCopyMessage("");

    try {
      const endpoint = `${process.env.REACT_APP_API_BASE_URL}/api/admin/generateJs/${pubId}`;
      const response = await axios.get(endpoint, {
        params: isBundle ? { isBundled: 1 } : {},
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });

      const fileUrl = isBundle ? response?.data?.js_url : response?.data?.json_url;

      if (response.status === 200 && fileUrl) {
        setGeneratedFile({ type: label, url: fileUrl, value: fileUrl, isTag: false });
      } else {
        setGenerateError(`${label} generated, but its URL was not found in the API response.`);
      }
    } catch (error) {
      console.error(`Error generating ${label}:`, error);
      if (error.response?.status === 401) {
        localStorage.removeItem("token");
        navigate("/admin");
        return;
      }
      setGenerateError(error.response?.data?.message || `Failed to generate ${label}.`);
    } finally {
      setGeneratingType(null);
    }
  };

  const handleCopyGeneratedUrl = async () => {
    if (!generatedFile?.value) return;
    try {
      await navigator.clipboard.writeText(generatedFile.value);
      setCopyMessage(generatedFile.isTag ? "Tag copied!" : "URL copied!");
    } catch (error) {
      console.error("Unable to copy URL:", error);
      setCopyMessage("Copy failed. You can select and copy the URL manually.");
    }
  };

  useEffect(() => {
    setGeneratedFile(null);
    setGenerateError("");
    setCopyMessage("");
    alladList();
  }, [pubId]);

  // A row-level Generate Tag button should bring the result into view.
  useEffect(() => {
    if (generatedFile?.isTag) {
      generatedResultRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [generatedFile]);

  return (
    <section className="ad-list-page">
      <div className="ad-list-container">

        {/* ==========================
            HEADER
        =========================== */}
        <div className="ad-list-header">
          <div>
            <h2 className="ad-list-title">Ad List</h2>
            <p className="ad-list-subtitle">
              Manage ad placements and publisher settings
            </p>
          </div>
        </div>

        {message && (
          <div className="alert alert-info ad-list-alert">
            {message}
          </div>
        )}

        {/* ==========================
            ACTIONS + FILTER
        =========================== */}
        <div className="ad-list-toolbar">

          <div className="ad-list-actions">

            <button
              className="ad-action-btn ad-action-success"
              onClick={() => navigate(`/create-ad/${pubId}`)}
            >
              <span>+</span>
              Create Ad Placement
            </button>

            <button
              className="ad-action-btn ad-action-warning"
              onClick={() => navigate(`/other-setting/${pubId}`)}
            >
              <span>+</span>
              Create Powered by Settings
            </button>

            <button
              className="ad-action-btn ad-action-primary"
              disabled={Boolean(generatingType)}
              onClick={() => {
                if (window.confirm("Do you want to generate JSON?")) {
                  handleGenerate("json");
                }
              }}
            >
              {generatingType === "json" ? "Generating JSON..." : "Generate JSON"}
            </button>

            <button
              className="ad-action-btn ad-action-secondary"
              disabled={Boolean(generatingType)}
              onClick={() => {
                if (window.confirm("Do you want to generate Bundle JS?")) {
                  handleGenerate("bundle");
                }
              }}
            >
              {generatingType === "bundle" ? "Generating Bundle JS..." : "Generate Bundle JS"}
            </button>

          </div>

          {/* Filter */}
          <div className="ad-status-filter">
            <label htmlFor="statusFilter">
              Filter by Ad Status
            </label>

            <select
              id="statusFilter"
              className="ad-status-select"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="all">All</option>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>

        </div>

        {/* Show only the URL corresponding to the generate button clicked. */}
        {generateError && (
          <div className="ad-generated-error" role="alert">
            {generateError}
          </div>
        )}

        {generatedFile && (
          <div className="ad-generated-result" role="status" ref={generatedResultRef}>
            <div className="ad-generated-result-title">
              {generatedFile.type} generated successfully
            </div>
            <div className="ad-generated-result-row">
              {generatedFile.isTag ? (
                <textarea
                  className="ad-generated-url-input"
                  value={generatedFile.value}
                  rows={4}
                  readOnly
                  aria-label="Generated div and script tags"
                  onFocus={(event) => event.target.select()}
                  style={{ flex: 1, minWidth: 0, minHeight: 125, fontFamily: "monospace" }}
                />
              ) : (
                <input
                  type="text"
                  className="ad-generated-url-input"
                  value={generatedFile.value}
                  readOnly
                  aria-label={`${generatedFile.type} URL`}
                  onFocus={(event) => event.target.select()}
                />
              )}
              <div>
                <button
                  type="button"
                  className="ad-generated-copy-btn"
                  onClick={handleCopyGeneratedUrl}
                style={{marginRight:"10px"}}>
                  {generatedFile.isTag ? "Copy Tag" : "Copy URL"}
                </button>
                <a
                  className="ad-generated-open-link"
                  href={generatedFile.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {generatedFile.isTag ? "Open JS" : "Open URL"}
                </a>
              </div>
            </div>
            {copyMessage && <p className="ad-generated-copy-message">{copyMessage}</p>}
          </div>
        )}

        {/* ==========================
            TABLE
        =========================== */}
        <div className="ad-table-card">

          <div className="ad-table-scroll">

            <table className="ad-list-table">

              <thead>
                <tr>
                  <th>S.N</th>
                  <th>PID Unit</th>
                  <th>Domain</th>
                  <th>Ad Unit</th>
                  <th>Ad Display Name</th>
                  <th>Ad Format</th>
                  <th>Device</th>
                  <th>Slot Size</th>
                  <th>Platform</th>
                  <th>Visible on Report</th>
                  <th>Ad Status</th>
                  <th>Action</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="12" className="ad-empty-state">
                      Loading ads...
                    </td>
                  </tr>
                ) : filteredAdList?.length > 0 ? (
                  filteredAdList.map((adlists, adListsInd) => (
                    <tr key={adListsInd}>

                      <td className="sn-column">
                        {adListsInd + 1}
                      </td>

                      <td>
                        <span className="pid-text">
                          {adlists.pid}
                        </span>
                      </td>

                      <td>
                        <span className="domain-text">
                          {adlists.domain}
                        </span>
                      </td>

                      <td>
                        {adlists.adunit_name}
                      </td>

                      <td>
                        {adlists.ad_display_name}
                      </td>

                      <td>
                        {adlists.design_format === "" ||
                        adlists.design_format == null
                          ? "Banner"
                          : adlists.design_format}
                      </td>

                      <td>
                        {adlists.device}
                      </td>

                      <td>
                        {adlists.slot_size}
                      </td>

                      <td>
                        {adlists.platform_type}
                      </td>

                      <td>
                        <span
                          className={`status-badge ${
                            adlists.is_visible === 0
                              ? "status-visible"
                              : "status-hidden"
                          }`}
                        >
                          {adlists.is_visible === 0
                            ? "Yes"
                            : adlists.is_visible === 1
                            ? "No"
                            : adlists.is_visible}
                        </span>
                      </td>

                      <td>
                        <span
                          className={`status-badge ${
                            adlists.status === 0
                              ? "status-active"
                              : "status-inactive"
                          }`}
                        >
                          {adlists.status === 0
                            ? "Active"
                            : adlists.status === 1
                            ? "Inactive"
                            : adlists.status}
                        </span>
                      </td>

                      <td className="action-column">
                        <button
                          className="ad-edit-btn"
                          onClick={() =>
                            navigate(
                              `/update-ad/${adlists.account_id}/${adlists.pid}`
                            )
                          }
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="ad-edit-btn"
                          style={{ marginLeft: 8, backgroundColor: "#2563eb", color: "#fff" }}
                          disabled={Boolean(generatingType)}
                          onClick={() => handleGenerateTag(adlists)}
                        >
                          Generate Tag
                        </button>
                      </td>

                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan="12"
                      className="ad-empty-state"
                    >
                      No data available
                    </td>
                  </tr>
                )}
              </tbody>

            </table>

          </div>

        </div>

      </div>
    </section>
  );
};

export default AdList;